import { useState, useEffect } from 'react';
import {
    Box, Paper, Button, Typography, List, ListItemButton,
    ListItemIcon, ListItemText, Divider, Tabs, Tab,
    useTheme, useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CategoryIcon from '@mui/icons-material/Category';
import QrCodeIcon from '@mui/icons-material/QrCode';
import StraightenIcon from '@mui/icons-material/Straighten';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import HSNModule from './HSNModule.jsx';
import TaxModule from './TaxModule.jsx';
import TDSDetails from './TDSDetails.jsx';
import DepreciationDetails from './DepreciationDetails.jsx';
import UnitModule from './UnitModule.jsx';
import StatusModule from './StatusModule.jsx';
import SourceModule from './SourceModule.jsx';
import TransportModule from './TransportModule.jsx';
import SeriesModule from './SeriesModule.jsx';
import CurrencyDetail2 from './CurrencyDetail2.jsx';
import ProdGroupDetail2 from './ProdGroupDetails2.jsx';
import AccGrpDetails from './AccGrpDetails.jsx';
import ConsignerMaster from './ConsignerMaster.jsx';
import TermsConditionModule from './TermsConditionModule.jsx';
import LoadingOverlay from '../components/LoadingOverlay.jsx';

// index = the case number `renderModule`'s switch uses for that module —
// kept stable even when the array is filtered by plan below.
const tabOptions = [
    { label: 'Account Group',     icon: <AccountTreeIcon fontSize="small" /> },
    { label: 'Tax',               icon: <ReceiptIcon fontSize="small" /> },
    { label: 'TDS',               icon: <AccountBalanceIcon fontSize="small" />, accountingOnly: true },
    { label: 'Depreciation',      icon: <TrendingDownIcon fontSize="small" />, accountingOnly: true },
    { label: 'Series',            icon: <FormatListNumberedIcon fontSize="small" /> },
    { label: 'Currency',          icon: <CurrencyExchangeIcon fontSize="small" />, accountingOnly: true },
    { label: 'Product Group',     icon: <CategoryIcon fontSize="small" /> },
    { label: 'HSN',               icon: <QrCodeIcon fontSize="small" /> },
    { label: 'Unit',              icon: <StraightenIcon fontSize="small" /> },
    { label: 'Transport',         icon: <LocalShippingIcon fontSize="small" /> },
    { label: 'Consigner',         icon: <BusinessIcon fontSize="small" /> },
    { label: 'Terms & Conditions',icon: <GavelIcon fontSize="small" /> },
].map((tab, index) => ({ ...tab, key: index }));

const CompanyDataManager = ({ companyId, planName, setSnackbar, onBack, connectionString }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [mode, setMode] = useState('comp');
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));  // < 600px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));    // ≥ 900px

    // "Billing" plan -> hide TDS, Depreciation, Currency. "Accounting" plan
    // (or any other/unknown plan, e.g. planName not yet resolved) -> show
    // every module, unchanged.
    const visibleTabOptions = planName === 'Billing'
        ? tabOptions.filter(tab => !tab.accountingOnly)
        : tabOptions;

    // If the currently-selected module isn't visible for this plan (e.g. the
    // company being edited turned out to be on the Billing plan after all),
    // fall back to the first module the plan allows.
    useEffect(() => {
        if (visibleTabOptions.length > 0 && !visibleTabOptions.some(t => t.key === selectedTab)) {
            setSelectedTab(visibleTabOptions[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTabOptions]);

    const moduleProps = { companyId, connectionString, setSnackbar, mode, setMode, setIsLoading };

    const renderModule = () => {
        switch (selectedTab) {
            case 0:  return <AccGrpDetails {...moduleProps} />;
            case 1:  return <TaxModule {...moduleProps} />;
            case 2:  return <TDSDetails {...moduleProps} />;
            case 3:  return <DepreciationDetails {...moduleProps} />;
            case 4:  return <SeriesModule {...moduleProps} />;
            case 5:  return <CurrencyDetail2 {...moduleProps} />;
            case 6:  return <ProdGroupDetail2 {...moduleProps} />;
            case 7:  return <HSNModule {...moduleProps} />;
            case 8:  return <UnitModule {...moduleProps} />;
            case 9:  return <TransportModule {...moduleProps} />;
            case 10: return <ConsignerMaster {...moduleProps} />;
            case 11: return <TermsConditionModule {...moduleProps} />;
            default: return null;
        }
    };

    return (
        <Box sx={{ px: { xs: 0, sm: 1, md: 2 }, py: { xs: 0, md: 1 } }}>

            {/* ── Header ─────────────────────────────────────────────── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: { xs: 1.5, sm: 0 }, py: { xs: 1.5, md: 1.5 },
                mb: { xs: 0.5, md: 1.5 }
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    variant="outlined"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                    {isMobile ? 'Back' : 'Back to Company'}
                </Button>
                <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    fontWeight={700}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    Company Data Management
                </Typography>
            </Box>

            {/* ── Mobile: horizontal scrollable chip-style nav ────────── */}
            {isMobile && (
                <Box sx={{
                    display: 'flex', gap: 0.75, overflowX: 'auto',
                    pb: 1.5, px: 1.5,
                    '&::-webkit-scrollbar': { height: 3 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
                }}>
                    {visibleTabOptions.map((tab) => (
                        <Box
                            key={tab.key}
                            onClick={() => setSelectedTab(tab.key)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.6,
                                px: 1.4, py: 0.75,
                                borderRadius: 5,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                bgcolor: selectedTab === tab.key ? 'primary.main' : 'background.paper',
                                color:   selectedTab === tab.key ? '#fff' : 'text.secondary',
                                border: '1px solid',
                                borderColor: selectedTab === tab.key ? 'primary.main' : 'divider',
                                boxShadow: selectedTab === tab.key ? 2 : 0,
                                transition: 'all 0.2s',
                                '& svg': { fontSize: '0.95rem' },
                            }}
                        >
                            {tab.icon}
                            <Typography variant="caption" fontWeight={selectedTab === tab.key ? 700 : 400} sx={{ fontSize: '0.75rem' }}>
                                {tab.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── Tablet: scrollable MUI Tabs ─────────────────────────── */}
            {!isMobile && !isDesktop && (
                <Tabs
                    value={selectedTab}
                    onChange={(_, v) => setSelectedTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1, borderColor: 'divider', mb: 2,
                        '& .MuiTab-root': { minWidth: 90, fontSize: '0.78rem', py: 1.2 },
                        '& .MuiTab-iconWrapper': { mb: '2px !important' },
                    }}
                >
                    {visibleTabOptions.map((tab) => (
                        <Tab
                            key={tab.key}
                            value={tab.key}
                            label={tab.label}
                            icon={tab.icon}
                            iconPosition="start"
                            sx={{ gap: 0.5 }}
                        />
                    ))}
                </Tabs>
            )}

            {/* ── Desktop: sidebar + content ──────────────────────────── */}
            {isDesktop ? (
                <Paper
                    elevation={2}
                    sx={{
                        display: 'flex',
                        borderRadius: 2,
                        overflow: 'hidden',
                        minHeight: 560,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {/* Sidebar nav */}
                    <Box sx={{
                        width: 210,
                        flexShrink: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        display: 'flex',
                        flexDirection: 'column',
                        pt: 1,
                        pb: 2,
                    }}>
                        <Typography
                            variant="caption"
                            sx={{ px: 2, pb: 1, pt: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem' }}
                        >
                            Modules
                        </Typography>
                        <Divider sx={{ mb: 0.5 }} />
                        <List dense disablePadding sx={{ flex: 1 }}>
                            {visibleTabOptions.map((tab) => (
                                <ListItemButton
                                    key={tab.key}
                                    selected={selectedTab === tab.key}
                                    onClick={() => setSelectedTab(tab.key)}
                                    sx={{
                                        mx: 0.75,
                                        my: 0.15,
                                        borderRadius: 1.5,
                                        py: 0.9,
                                        transition: 'all 0.15s',
                                        '&.Mui-selected': {
                                            bgcolor: 'primary.main',
                                            color: '#fff',
                                            boxShadow: 1,
                                            '& .MuiListItemIcon-root': { color: '#fff' },
                                            '&:hover': { bgcolor: 'primary.dark' },
                                        },
                                        '&:not(.Mui-selected):hover': {
                                            bgcolor: 'primary.50',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: 32,
                                        color: selectedTab === tab.key ? '#fff' : 'primary.main',
                                    }}>
                                        {tab.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={tab.label}
                                        primaryTypographyProps={{
                                            fontSize: '0.82rem',
                                            fontWeight: selectedTab === tab.key ? 700 : 400,
                                            noWrap: true,
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>

                    {/* Module content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: { md: 1.5, lg: 2 }, minWidth: 0 }}>
                        {/* Active module breadcrumb */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                                {tabOptions[selectedTab]?.icon}
                            </Box>
                            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                                {tabOptions[selectedTab]?.label}
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 1.5 }} />
                        {renderModule()}
                    </Box>
                </Paper>
            ) : (
                /* Mobile + Tablet: content below nav */
                <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
                    {renderModule()}
                </Box>
            )}

            {/* Loading Overlay */}
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
        </Box>
    );
};

export default CompanyDataManager;
